import classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { ProductTileModel } from "@api/mappers/products.mapper";

import { ProductSliderSources, ProductVisibilityTypes } from "@utils/enums";
import { useProductCartContext } from "@utils/hooks/cart.hook";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";
import { SwiperNavButton } from "@components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { ProductSlidersRenditions } from "@components/Product/ProductImage/mappedRenditions";
import ProductTileComponent from "@components/Product/ProductTile/productTile.component";
import { ProductTileLayout } from "@components/Product/ProductTile/productTile.config";

import "./productSlider.scss";

const VisibilityTypeToFilter = new Set([
    ProductVisibilityTypes.Offline,
    ProductVisibilityTypes.NotAvailable,
    ProductVisibilityTypes.TemporaryNotAvailable,
]);

const isVisible = (product: ProductTileModel) =>
    !VisibilityTypeToFilter.has(product.productVisibilityType);

type Props = {
    title: string;
    productsource: string;
    showButton: boolean;
    showCategoryLink: boolean;
    useInsidePadding: boolean;
    amountOfFeatures: number;
    products: Array<ProductTileModel>;
    breakpointslayoutsettings: any;
    deleteFromHistory?: any;
    deleteRecentsHistory?: any;
    analyticsContext?: {
        list: string;
        listTitle: string;
    };
    ssrLayoutClasses?: string;
    lazyLoad: boolean;
};

const ProductSlider = ({
    title,
    productsource,
    showButton,
    showCategoryLink,
    amountOfFeatures,
    products,
    breakpointslayoutsettings,
    deleteFromHistory,
    deleteRecentsHistory,
    analyticsContext,
    ssrLayoutClasses,
    lazyLoad,
    useInsidePadding,
}: Props) => {
    const { t } = useGranite();
    const [allowPrevious, setAllowPrevious] = useState(false);
    const [allowNext, setAllowNext] = useState(true);
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);
    const cartContext = useProductCartContext();

    useEffect(() => {
        updateButtons();
    }, []);

    const updateButtons = () => {
        if (mainSwiper) {
            setAllowPrevious(!mainSwiper.isBeginning);
            setAllowNext(!mainSwiper.isEnd);
        }
    };

    const productSourceIsRecent = ProductSliderSources.recent === productsource;

    return (
        <div
            className={classnames(
                productSourceIsRecent && "productSlider-fullwidth pb-2",
            )}
        >
            <div
                className={classnames(
                    ssrLayoutClasses && ssrLayoutClasses,
                    productSourceIsRecent && "productSlider-recents",
                    productSourceIsRecent && "container p-md-4",
                )}
            >
                <div className="row m-0 d-md-flex flex-row mb-3 mb-md-0">
                    {title && (
                        <h2 className={classnames("mr-3 my-2 flex-shrink-1")}>
                            {title}
                        </h2>
                    )}
                    <div
                        className={classnames(
                            "d-flex align-items-center flex-wrap flex-grow-1 ",
                        )}
                    >
                        {productSourceIsRecent && deleteRecentsHistory && (
                            <a
                                className="mt-1"
                                href="#"
                                onClick={e => {
                                    e.preventDefault();
                                    deleteRecentsHistory();
                                }}
                            >
                                {t("productSlider.link.deleteHistory")}
                            </a>
                        )}
                    </div>
                </div>
                <div
                    className={classnames(
                        "fullwidth__slider",
                        "d-flex align-items-center w-100",
                        productSourceIsRecent &&
                            !allowNext &&
                            !allowPrevious &&
                            "justify-content-center",
                    )}
                >
                    {(allowPrevious || allowNext) && (
                        <SwiperNavButton
                            innerRef={navigationPrevRef}
                            className={"d-none d-md-block"}
                            disabled={!allowPrevious}
                            icon="left"
                            onClick={() => {
                                if (allowPrevious && mainSwiper) {
                                    mainSwiper.slidePrev();
                                }
                            }}
                        />
                    )}
                    <Swiper
                        modules={[Navigation]}
                        onSwiper={setMainSwiper}
                        updateOnWindowResize={true}
                        resizeObserver={true}
                        onSlideChange={() => updateButtons()}
                        spaceBetween={productSourceIsRecent ? 7 : 20}
                        slidesPerView={1}
                        slidesPerGroup={1}
                        navigation={{
                            nextEl: navigationNextRef.current,
                            prevEl: navigationPrevRef.current,
                        }}
                        breakpoints={breakpointslayoutsettings}
                    >
                        {products
                            .filter(isVisible)
                            .map((product, index: number) => (
                                <SwiperSlide
                                    key={product.sku}
                                    className="productSlider-slide ssr-slide"
                                >
                                    <ProductTileComponent
                                        product={product}
                                        cart={cartContext?.cart}
                                        allowInstantAddToCart={
                                            !!cartContext?.allowInstantAddToCart
                                        }
                                        lazyLoadImage={lazyLoad}
                                        productTileConfig={{
                                            amountOfFeatures: amountOfFeatures,
                                            imageRenditions:
                                                ProductSlidersRenditions,
                                            border: false,
                                            tilePadding: useInsidePadding,
                                            components:
                                                ProductTileLayout.SLIDER(
                                                    showButton,
                                                    showCategoryLink,
                                                    amountOfFeatures > 0,
                                                ),
                                        }}
                                        analyticsContext={
                                            analyticsContext && {
                                                ...analyticsContext,
                                                placement: index + 1,
                                            }
                                        }
                                    >
                                        {productSourceIsRecent &&
                                            deleteFromHistory && (
                                                <Button
                                                    type="button"
                                                    test="productSlider-button-delete"
                                                    className="productSlider-deleteButton d-none d-lg-block"
                                                    color={"transparent"}
                                                    aria-label={t(
                                                        "accessibility.productSlider.delete",
                                                    )}
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        deleteFromHistory(
                                                            product.sku,
                                                        );
                                                    }}
                                                >
                                                    <i className="las la-times" />
                                                </Button>
                                            )}
                                    </ProductTileComponent>
                                </SwiperSlide>
                            ))}
                    </Swiper>
                    {(allowPrevious || allowNext) && (
                        <SwiperNavButton
                            innerRef={navigationNextRef}
                            className={"d-none d-md-block"}
                            disabled={!allowNext}
                            icon="right"
                            onClick={() => {
                                if (allowNext && mainSwiper)
                                    mainSwiper.slideNext();
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export { ProductSlider };
